import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  QrCodeIcon, 
  ShoppingCart, 
  Star,
  Package,
  Truck,
  Shield,
  Heart,
  ArrowRight,
  CheckCircle,
  Download,
  FileText,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ProductStore() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCustomerServiceDialog, setShowCustomerServiceDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.getPhysicalProducts();
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleAddToCart = (product) => {
    if (!user) {
      toast.error('Please sign in to purchase products');
      navigate('/auth');
      return;
    }

    // For now, simulate adding to cart
    toast.success(`${product.name} added to cart!`);
    // In full implementation, this would add to cart state/context
  };

  const handleGeneratePoster = async () => {
    if (!user) {
      toast.error('Please sign in to generate a poster');
      navigate('/auth');
      return;
    }

    try {
      setLoading(true);
      const response = await api.generateQRPoster();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `iComment-Poster-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Poster downloaded! The QR code will be activated on first scan.');
    } catch (error) {
      console.error('Failed to generate poster:', error);
      toast.error('Failed to generate poster');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCustomerServicePoster = async (event, format = 'pdf') => {
    event.preventDefault();
    
    // Get the form element - either from form submission or from button's parent form
    const formElement = event.target.form || event.target.closest('form');
    if (!formElement) {
      toast.error('Form not found');
      return;
    }
    
    const formData = new FormData(formElement);
    const businessName = formData.get('business_name');
    const customText = formData.get('custom_text');

    try {
      setLoading(true);
      
      let response, blob, mimeType, fileExtension;
      
      if (format === 'word') {
        // Generate WORD format
        response = await api.generatePublicCustomerServicePosterWord(businessName, customText);
        const wordData = atob(response.data.word);
        const bytes = new Uint8Array(wordData.length);
        for (let i = 0; i < wordData.length; i++) {
          bytes[i] = wordData.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        fileExtension = 'docx';
      } else {
        // Generate PDF format
        response = await api.generatePublicCustomerServicePoster(businessName, customText);
        const pdfData = atob(response.data.pdf);
        const bytes = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          bytes[i] = pdfData.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'application/pdf' });
        mimeType = 'application/pdf';
        fileExtension = 'pdf';
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Customer-Service-${businessName.replace(/\s+/g, '-')}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Poster downloaded as ${format.toUpperCase()}! QR code (${response.data.uid}) will be claimed by first scanner.`);
      setShowCustomerServiceDialog(false);
    } catch (error) {
      console.error('Failed to generate customer service poster:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate poster');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSingleQR = async () => {
    if (!user) {
      toast.error('Please sign in to generate a QR code');
      navigate('/auth');
      return;
    }

    try {
      setLoading(true);
      const response = await api.generateSingleQR();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `iComment-QR-${Date.now()}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('QR code downloaded successfully!');
      
      // Refresh user data to update QR codes count
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      console.error('QR generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePurchaseQRPack = async (product) => {
    if (!user) {
      toast.error('Please sign in to purchase QR codes');
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      // Create purchase order for digital product
      const orderResponse = await api.purchaseQRPack({ pack_id: product.id });
      
      // Simulate PayPal payment for now
      // In production, this would integrate with actual PayPal
      setTimeout(async () => {
        try {
          const paymentResponse = await api.completePayment(orderResponse.data.order_id, {
            paypal_payment_id: `DEMO_${Date.now()}`
          });
          
          toast.success('Purchase completed! Check your dashboard for QR codes.');
          navigate('/dashboard');
        } catch (error) {
          console.error('Payment failed:', error);
          toast.error('Payment failed. Please try again.');
        } finally {
          setLoading(false);
        }
      }, 2000); // Simulate payment processing time

    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
      setLoading(false);
    }
  };
  
  const handleProductAction = (product) => {
    if (product.product_type === 'digital') {
      handlePurchaseQRPack(product);
    } else {
      handleAddToCart(product);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'stickers': return '🏷️';
      case 'cards': return '💳';
      case 'keychains': return '🔑';
      case 'bundles': return '📦';
      case 'digital_qr_pack': return '📱';
      default: return '🛍️';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'stickers': return 'bg-blue-100 text-blue-800';
      case 'cards': return 'bg-green-100 text-green-800';
      case 'keychains': return 'bg-purple-100 text-purple-800';
      case 'bundles': return 'bg-orange-100 text-orange-800';
      case 'digital_qr_pack': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const categories = [
    { id: 'all', name: 'All Products', icon: <Package className="h-4 w-4" /> },
    { id: 'digital_qr_pack', name: 'Digital QR Packs', icon: <QrCodeIcon className="h-4 w-4" /> },
    { id: 'stickers', name: 'Stickers', icon: '🏷️' },
    { id: 'cards', name: 'Business Cards', icon: '💳' },
    { id: 'keychains', name: 'Keychains', icon: '🔑' },
    { id: 'bundles', name: 'Bundles & Kits', icon: '📦' },
  ];
  
  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <Link to="/" className="flex items-center space-x-2">
                <QrCodeIcon className="h-8 w-8 text-teal-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">iComment Store</h1>
                  <p className="text-sm text-gray-600">Anonymous messaging through QR codes</p>
                </div>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    Sign In
                  </Button>
                </Link>
              )}
              <Button variant="outline" className="border-teal-600 text-teal-600">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Cart (0)
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar - Categories */}
          <aside className="w-64 flex-shrink-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {typeof category.icon === 'string' ? (
                        <span className="text-xl">{category.icon}</span>
                      ) : (
                        category.icon
                      )}
                      <span className="font-medium">{category.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Loading products...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Customer Service Poster - NO LOGIN REQUIRED - Always show */}
              <Card className="hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
                <CardHeader>
                  <div className="flex justify-between items-start mb-4">
                    <Badge className="bg-purple-100 text-purple-800">
                      <FileText className="h-3 w-3 mr-1" /> NO LOGIN
                    </Badge>
                    <Badge variant="outline" className="text-purple-600 border-purple-200 animate-pulse">
                      ⭐ Popular
                    </Badge>
                  </div>
                  
                  {/* Product Image */}
                  <div className="w-full h-48 bg-white rounded-lg flex items-center justify-center mb-4 border-2 border-dashed border-purple-300">
                    <div className="text-center p-4">
                      <FileText className="h-16 w-16 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Business Poster</p>
                    </div>
                  </div>
                  
                  <CardTitle className="text-xl">Customer Service Poster</CardTitle>
                  <CardDescription>
                    Perfect for businesses! No login needed. First person to scan claims ownership.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 text-sm">Perfect for:</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-purple-600 mr-2" />
                        <span>Restaurant feedback</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-purple-600 mr-2" />
                        <span>Store customer service</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-purple-600 mr-2" />
                        <span>Office reception</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-purple-600 mr-2" />
                        <span>First scanner becomes owner</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <div className="text-2xl font-bold text-purple-600">
                      FREE
                    </div>
                    <Button 
                      onClick={() => setShowCustomerServiceDialog(true)}
                      disabled={loading}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Create Now
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Single QR Code */}
              <Card className="hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
                <CardHeader>
                  <div className="flex justify-between items-start mb-4">
                    <Badge className="bg-blue-100 text-blue-800">
                      <QrCodeIcon className="h-3 w-3 mr-1" /> Digital
                    </Badge>
                    <Badge variant="outline" className="text-blue-600 border-blue-200">
                      1 QR Code
                    </Badge>
                  </div>
                  
                  {/* Product Image */}
                  <div className="w-full h-48 bg-white rounded-lg flex items-center justify-center mb-4 border-2 border-dashed border-blue-300">
                    <div className="text-center p-4">
                      <QrCodeIcon className="h-16 w-16 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">PNG Image File</p>
                    </div>
                  </div>
                  
                  <CardTitle className="text-xl">Single QR Code</CardTitle>
                  <CardDescription>
                    Generate a single QR code instantly. High-quality PNG perfect for digital or print use.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 text-sm">Features:</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-blue-600 mr-2" />
                        <span>High-resolution PNG</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-blue-600 mr-2" />
                        <span>Immediately active</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-blue-600 mr-2" />
                        <span>Your account linked</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <div className="text-2xl font-bold text-blue-600">
                      Per Limit
                    </div>
                    <Button 
                      onClick={handleGenerateSingleQR}
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {loading ? 'Generating...' : 'Generate QR'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* All Products - Digital & Physical */}
              {filteredProducts.map((product) => (
                <Card key={product.id} className="hover:shadow-lg transition-all duration-300 bg-white overflow-hidden" data-testid={`product-${product.id}`}>
                  {/* Product Image with Badge */}
                  <div className="relative">
                    <div className="w-full h-56 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center">
                          {product.product_type === 'digital' ? (
                            <QrCodeIcon className="h-20 w-20 text-teal-600 mx-auto" />
                          ) : (
                            <>
                              <div className="text-5xl mb-2">{getCategoryIcon(product.category)}</div>
                              <QrCodeIcon className="h-16 w-16 text-gray-400 mx-auto" />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Featured Badge */}
                    {product.specifications?.popular && (
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-amber-500 text-white border-0">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Featured
                        </Badge>
                      </div>
                    )}
                    {/* Best Value Badge */}
                    {product.product_type === 'digital' && product.price < 15 && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-pink-600 text-white border-0">
                          Best Value
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-4">
                    {/* Product Title */}
                    <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    
                    {/* Product Description */}
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.description}
                    </p>
                    
                    {/* Star Rating */}
                    <div className="flex items-center space-x-1 mb-3">
                      {[1,2,3,4].map((star) => (
                        <Star key={star} className="w-4 h-4 text-amber-400 fill-current" />
                      ))}
                      <Star className="w-4 h-4 text-gray-300" />
                      <span className="text-xs text-gray-600 ml-1">(127 reviews)</span>
                    </div>
                    
                    {/* Price and Action */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div>
                        <div className="text-2xl font-bold text-teal-600">
                          ${product.price}
                        </div>
                        {product.product_type === 'digital' && product.qr_codes_included > 0 && (
                          <div className="text-xs text-gray-500">
                            {product.qr_codes_included} QR codes
                          </div>
                        )}
                      </div>
                      <Button 
                        onClick={() => handleProductAction(product)}
                        disabled={loading}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid={`product-action-${product.id}`}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {product.product_type === 'digital' ? 'Buy Now' : 'Add to Cart'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Message when no physical products available */}
              {products.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Physical products coming soon! Try our free Customer Service Poster above.</p>
                </div>
              )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Customer Service Poster Dialog */}
      <Dialog open={showCustomerServiceDialog} onOpenChange={setShowCustomerServiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Customer Service Poster</DialogTitle>
            <DialogDescription>
              Generate a free poster for your business. No login required! The first person to scan will become the owner.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerateCustomerServicePoster} className="space-y-4">
            <div>
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                name="business_name"
                placeholder="e.g., Joe's Coffee Shop"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="custom_text">Custom Text</Label>
              <Textarea
                id="custom_text"
                name="custom_text"
                placeholder="e.g., Scan to leave feedback or contact us"
                defaultValue="Scan to leave feedback or contact us"
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-900">
                <strong>📌 Note:</strong> This QR code will be unclaimed until someone scans it. The first person to scan can claim ownership and manage messages.
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCustomerServiceDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                onClick={(e) => handleGenerateCustomerServicePoster(e, 'pdf')}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                <Download className="mr-2 h-4 w-4" />
                {loading ? 'Generating...' : 'Download PDF'}
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                onClick={(e) => handleGenerateCustomerServicePoster(e, 'word')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                <FileText className="mr-2 h-4 w-4" />
                {loading ? 'Generating...' : 'Download WORD'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}